import { useEffect, useState } from "react";
import { Coins, Gift, BadgePercent, Repeat, Users, Hourglass, Wallet } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { StatCard } from "@/components/common";
import { api, brl, errMsg } from "@/lib/api";
import { fmtUnits, lastDays } from "@/lib/loyalty";
import { PeriodPicker } from "./PeriodPicker";
import { toast } from "sonner";

const tick = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };

function Chart({ series, mode }) {
  return (
    <div className="card-surface p-5" data-testid="loyalty-chart">
      <p className="eyebrow mb-3">{mode === "cashback" ? "Cashback por dia (R$)" : "Pontos por dia"}</p>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ left: 0, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={tick} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tick={tick} tickLine={false} axisLine={false} width={44} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="earned" name="Distribuídos" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="redeemed" name="Resgatados" stroke="#14b8a6" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function LoyaltyOverview({ cfg }) {
  const [period, setPeriod] = useState(() => lastDays(7));
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/loyalty/overview", { params: period }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [period]);
  const cash = cfg.mode === "cashback";
  const u = (v) => fmtUnits(cfg.mode, v);
  return (
    <div className="space-y-5" data-testid="loyalty-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Selecione um período e veja o desempenho do seu programa de fidelidade.</p>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      {d && (<>
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <StatCard label={cash ? "Cashback distribuído" : "Pontos distribuídos"} value={u(d.distributed)} icon={Coins} tone="warning" testId="loyalty-kpi-distributed" />
          <StatCard label={cash ? "Cashback usado" : "Pontos resgatados"} value={u(d.redeemed)} icon={Gift} tone="teal" testId="loyalty-kpi-redeemed" />
          <StatCard label="Em descontos" value={brl(d.discounts)} icon={BadgePercent} tone="success" testId="loyalty-kpi-discounts" />
          <StatCard label="Resgates" value={d.redemptions} icon={Repeat} testId="loyalty-kpi-redemptions" />
        </div>
        <Chart series={d.series} mode={cfg.mode} />
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="Saldo em aberto" value={u(d.open_balance)} hint="Soma do saldo de todos os clientes" icon={Wallet} testId="loyalty-kpi-open" />
          <StatCard label="Clientes com saldo" value={d.customers_with_balance} icon={Users} tone="teal" testId="loyalty-kpi-customers" />
          <StatCard label="Aguardando pedido finalizar" value={u(d.pending)} hint="Entram no saldo quando o pedido é finalizado" icon={Hourglass} tone="warning" testId="loyalty-kpi-pending" />
        </div>
      </>)}
    </div>
  );
}
